import React, { useEffect } from 'react'
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import MagazineStageSimple from '../../Components/MagazineStageSimple/MagazineStageSimple';
import imgCapa from '../../assets/news-events/reliable_hydraulic_system/Capa.png';
import imgImagem1 from '../../assets/news-events/reliable_hydraulic_system/Imagem 1.png';
import imgImagem2 from '../../assets/news-events/reliable_hydraulic_system/Imagem 2.png';
import imgImagem3 from '../../assets/news-events/reliable_hydraulic_system/Imagem 3.png';
import MagazineTextBlock from '../../Components/MagazineTextBlock/MagazineTextBlock';

const ReliableHydraulicSystem = () => {
    useEffect(() => {
        window.scrollTo(0, 0); // Scroll to top on component mount
    }, []);

    const { t } = useTranslation();
    const ogTitle = t('7 steps to a reliable hydraulic system');
    const ogDescription = t('A 7-point checklist to keep your hydraulic system safer, more efficient and more reliable: pressure, temperature, fluid compatibility, signs of wear, bend radius, twisting and abrasion, and routine inspections.');
    const ogImage = `https://www.hansa-flex.com.br/${imgCapa}`;
    const ogUrl = `https://www.hansa-flex.com.br/reliable-hydraulic-system`;

    return (
        <div className="chh-article">
            <Helmet>
                <meta property="og:title" content={ogTitle} />
                <meta property="og:description" content={ogDescription} />
                <meta property="og:image" content={ogImage} />
                <meta property="og:url" content={ogUrl} />
                <meta property="og:type" content="News" />
            </Helmet>

            <MagazineStageSimple
                imageSrc={imgCapa}
                imagePosition="afterTitle"
                imageAlt="7 steps to a reliable hydraulic system"
                title={"7 steps to a reliable hydraulic system"}
                text={[
                    "A small failure in the hydraulic system can turn into a big headache for maintenance. And often, the problem starts with something that could have been identified earlier: a worn hose, an unsuitable fitting or even a bend outside the recommended range.",
                    "The good news is that a few simple precautions can make a big difference. We have prepared a checklist with 7 points that help keep your hydraulic system safer, more efficient and more reliable. Take a look!",
                ]}
                readingTimeMinutes={5}
            />

            <MagazineTextBlock
                title="Why does hydraulic system reliability deserve attention?"
                links={["https://www.hansa-flex.com.br/preventative-maintainance"]}
                paragraphs={[
                    "Anyone who works in industrial maintenance knows: when a machine stops unexpectedly, the problem goes far beyond the equipment.",
                    "Production is delayed, the team needs to act urgently and costs start to rise.",
                    "That is why taking care of the hydraulic system before something goes wrong is always the best approach.",
                    "With the right components, regular inspections and a good <1>preventive maintenance</1> routine, it is possible to reduce the risk of failures and extend the service life of the equipment.",
                    "And this is exactly where HANSA-FLEX can help, offering hoses, fittings and hydraulic solutions suited to the needs of each operation.",
                ]}
            />

            <MagazineTextBlock
                title="Checklist: what should you check in your hydraulic system?"
            />

            <MagazineTextBlock
                imageSrc={imgImagem1}
                imagePosition="beforeTitle"
                imageAlt="Is the working pressure within the limit?"
                title="1. Is the working pressure within the limit?"
                paragraphs={[
                    "A hose that looks suitable is not always really prepared for the system pressure.",
                    "Before installing or replacing a component, check that it can withstand the real working conditions, including possible pressure peaks.",
                    "It is also worth checking the hose diameter. An unsuitable choice can increase pressure losses and impair the performance of the equipment.",
                ]}
            />

            <MagazineTextBlock
                title="2. Is the temperature suitable?"
                paragraphs={[
                    "Excessive heat can accelerate hose wear. Very low temperatures, on the other hand, can cause the material to lose elasticity and develop cracks.",
                    "Therefore, check that the hose used is compatible with both the fluid temperature and the ambient temperature.",
                ]}
            />

            <MagazineTextBlock
                title="3. Is the hose compatible with the fluid?"
                paragraphs={[
                    "Hydraulic oil, water, glycols and other fluids have different characteristics.",
                    "Using an incompatible hose can compromise its durability and the safety of the operation.",
                    "If in doubt, it is worth confirming the technical specifications before choosing the component.",
                ]}
            />

            <MagazineTextBlock
                imageSrc={imgImagem2}
                imagePosition="beforeTitle"
                imageAlt="Are there signs of wear?"
                title="4. Are there signs of wear?"
                paragraphs={[
                    "Here, a good visual inspection can already reveal a lot.",
                    "Watch out for:",
                ]}
                bulletPoints={[
                    "Cuts, cracks and abrasion marks;",
                    "Bubbles or deformation in the hose;",
                    "Leaks at the fittings;",
                    "Corrosion or damage to the end fittings;",
                    "Signs of drying out.",
                ]}
                paragraphs2={[
                    "These problems should not be ignored. Depending on their severity, replacement is necessary to avoid bigger failures.",
                ]}
            />

            <MagazineTextBlock
                title="5. Is the minimum bend radius being respected?"
                paragraphs={[
                    "A hose that is bent too much may keep working for a while, but that does not mean it is working correctly.",
                    "When the <0>minimum bend radius</0> is not respected, the hose structure is subjected to additional stress, increasing the risk of failure.",
                    "So pay particular attention to the bends close to the end fittings and to the points where there is movement.",
                ]}
            />

            <MagazineTextBlock
                title="6. Is there twisting or friction during operation?"
                paragraphs={[
                    "This is a detail that can go unnoticed during installation.",
                    "A hose that is twisted or in constant contact with other parts tends to wear out more quickly.",
                    "Over time, these stresses can compromise the hose structure, shorten its service life and increase the risk of leaks and failures.",
                    "It is worth checking how the hoses are routed and assessing the need for abrasion protection.",
                ]}
            />

            <MagazineTextBlock
                title="7. Are inspections part of the routine?"
                paragraphs={[
                    "There is no point in identifying problems only when the machine has already stopped.",
                    "Ideally, create an inspection routine, record the condition of the components and keep track of which hoses need more attention.",
                    "This way, the team can plan better, organize replacements and prevent small problems from becoming emergencies.",
                ]}
            />

            <MagazineTextBlock
                imageSrc={imgImagem3}
                imagePosition="beforeTitle"
                imageAlt="Take care today so you don't stop tomorrow"
                title="Take care today so you don't stop tomorrow"
                links={["https://www.hansa-flex.com.br/contact-us"]}
                paragraphs={[
                    "The reliability of a hydraulic system does not depend only on the quality of the parts. It also lies in day-to-day care, in choosing the right components and in paying attention to the first signs of wear.",
                    "With a well-structured preventive routine, your company gains more safety, predictability and peace of mind to keep the operation running.",
                    "<0>Count on HANSA-FLEX to keep your hydraulic system in good condition.</0> Our team offers specialized technical support and solutions in hoses and hydraulic components for different industrial applications.",
                    "<1>Contact HANSA-FLEX</1><0> and find the ideal solution for your operation.</0>",
                ]}
            />

            <div style={{ backgroundColor: '#f8f8f8' }}>
                <br />
                <br />
                <br />
            </div>

        </div>
    )
}

export default ReliableHydraulicSystem
